import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, CircularProgress, Alert, Chip, MenuItem, TablePagination,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { Add, ExpandMore } from '@mui/icons-material';
import { accountingApi } from '../../services/api';
import { JournalEntry, Account } from '../../types';

const JournalEntryList: React.FC = () => {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<{ accountId: string; debitAmount: string; creditAmount: string; memo: string }[]>([
    { accountId: '', debitAmount: '', creditAmount: '', memo: '' },
    { accountId: '', debitAmount: '', creditAmount: '', memo: '' },
  ]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [formError, setFormError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [jeRes, accRes] = await Promise.all([
        accountingApi.getJournalEntries(),
        accountingApi.getChartOfAccounts(),
      ]);
      setEntries(Array.isArray(jeRes.data) ? jeRes.data : jeRes.data?.data || []);
      const grouped = accRes.data;
      const flat: Account[] = [];
      if (grouped && typeof grouped === 'object' && !Array.isArray(grouped)) {
        Object.values(grouped).forEach((arr: any) => { if (Array.isArray(arr)) flat.push(...arr); });
      }
      setAccounts(flat);
    } catch (err: any) {
      setError('Failed to load journal entries.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAddDialog = () => {
    setEntryDate(new Date().toISOString().split('T')[0]);
    setDescription('');
    setLines([
      { accountId: '', debitAmount: '', creditAmount: '', memo: '' },
      { accountId: '', debitAmount: '', creditAmount: '', memo: '' },
    ]);
    setFormError('');
    setDialogOpen(true);
  };

  const addLine = () => setLines([...lines, { accountId: '', debitAmount: '', creditAmount: '', memo: '' }]);

  const updateLine = (idx: number, field: string, value: string) => {
    const updated = [...lines];
    (updated[idx] as any)[field] = value;
    setLines(updated);
  };

  const handleSave = async () => {
    const validLines = lines.filter(l => l.accountId);
    if (validLines.length === 0) { setFormError('At least one line with an account is required.'); return; }
    const totalDebits = validLines.reduce((s, l) => s + (Number(l.debitAmount) || 0), 0);
    const totalCredits = validLines.reduce((s, l) => s + (Number(l.creditAmount) || 0), 0);
    if (Math.abs(totalDebits - totalCredits) > 0.01) {
      setFormError(`Debits (${totalDebits.toFixed(2)}) must equal credits (${totalCredits.toFixed(2)}).`);
      return;
    }
    setSaving(true); setFormError('');
    try {
      await accountingApi.createJournalEntry({
        entryDate: new Date(entryDate).toISOString(),
        description: description.trim(),
        lines: validLines.map(l => ({
          accountId: Number(l.accountId),
          debitAmount: Number(l.debitAmount) || 0,
          creditAmount: Number(l.creditAmount) || 0,
          memo: l.memo,
        })),
      });
      await fetchData();
      setDialogOpen(false);
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to create journal entry.');
    } finally { setSaving(false); }
  };

  const formatCurrency = (v: number) => v ? `$${v.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '-';
  const displayed = entries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Journal Entries</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAddDialog}>New Entry</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          {displayed.map((je) => {
            const totalDebit = je.lines?.reduce((s, l) => s + l.debitAmount, 0) || 0;
            const totalCredit = je.lines?.reduce((s, l) => s + l.creditAmount, 0) || 0;
            return (
              <Accordion key={je.id} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', width: '100%', mr: 2 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, minWidth: 140 }}>{je.entryNumber}</Typography>
                    <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>{je.description}</Typography>
                    <Typography variant="body2" color="text.secondary">{new Date(je.entryDate).toLocaleDateString()}</Typography>
                    <Chip label={je.isPosted ? 'Posted' : 'Draft'} color={je.isPosted ? 'success' : 'default'} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Account</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Debit</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Credit</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Memo</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {je.lines?.map((l) => (
                        <TableRow key={l.id}>
                          <TableCell>{l.accountName || `#${l.accountId}`}</TableCell>
                          <TableCell align="right">{formatCurrency(l.debitAmount)}</TableCell>
                          <TableCell align="right">{formatCurrency(l.creditAmount)}</TableCell>
                          <TableCell>{l.memo}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Totals</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(totalDebit)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(totalCredit)}</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            );
          })}
          {entries.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">No journal entries.</Typography></Paper>
          )}
          <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={entries.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </>
      )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Journal Entry</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{formError}</Alert>}
          <TextField label="Entry Date" type="date" fullWidth margin="normal" value={entryDate} onChange={e => setEntryDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} disabled={saving} />
          <TextField label="Description" fullWidth margin="normal" value={description} onChange={e => setDescription(e.target.value)} disabled={saving} />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>Lines</Typography>
          {lines.map((line, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <TextField select size="small" sx={{ minWidth: 180 }} value={line.accountId} onChange={e => updateLine(idx, 'accountId', e.target.value)} disabled={saving}>
                {accounts.map(a => <MenuItem key={a.id} value={String(a.id)}>{a.accountCode} - {a.name}</MenuItem>)}
              </TextField>
              <TextField size="small" sx={{ width: 110 }} label="Debit" type="number" value={line.debitAmount} onChange={e => updateLine(idx, 'debitAmount', e.target.value)} disabled={saving} />
              <TextField size="small" sx={{ width: 110 }} label="Credit" type="number" value={line.creditAmount} onChange={e => updateLine(idx, 'creditAmount', e.target.value)} disabled={saving} />
              <TextField size="small" sx={{ flex: 1, minWidth: 120 }} label="Memo" value={line.memo} onChange={e => updateLine(idx, 'memo', e.target.value)} disabled={saving} />
            </Box>
          ))}
          <Button onClick={addLine} size="small" sx={{ mt: 1 }}>+ Add Line</Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={18} /> : undefined}>{saving ? 'Posting...' : 'Post Entry'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JournalEntryList;
